/* eslint-disable camelcase */
import { invisiblyApi } from '../api/invisibly-api';
import { ApiStatusData, BaseSuccessResponse, InvisiblyApiResponse } from "../../services/api/api-response.types";
import SurveyService from '../../services/surveys/survey.service';
import { FetchSurveyApiResponse, FetchSurveyData, FetchSurveyDataRequest } from '../../services/surveys/survey.types';
import { DEFAULT_ANSWER_TITLE, SURVEY_GRID_PAGE_SIZE } from '../../services/surveys/survey.constants';
import { APITagType } from '../api/invisibly-api.types';
import {
  Answer, Brand, ModerationCheckResults,
  ModerationResponse,
  Question, QuestionCreationData, QuestionUpdateData,
  Survey,
  SurveyAnswerJSON,
  SurveyAudience,
  SurveyAudienceCriteria,
  SurveyAudienceJSON,
  SurveyAudienceSmall,
  SurveyAudienceSmallJSON,
  SurveyCloneResponse,
  SurveyFull,
  SurveyFullJSON,
  SurveyJSON,
  SurveyQuestionJSON, SurveyQuestionType, SurveyStats, SurveyStatsJSON,
  SurveyStatus, SurveySummaryData, SurveySummaryDataJSON,
  toSurveyStatus,
  UploadImage,
  SurveyToast,
  SurveyResponderJSON,
  SurveyResponder,
  PulsePollingConfigResponse,
  SurveyTypeResponse,
  SurveyDemographicsResponse,
  SurveyDemographicRecord,
} from './survey.types';
import { omitNullValues } from '../../helpers/request.helpers';
import { UpdateAnswerParams } from "../admin/survey/survey.types";
import { createShare } from "../../services/surveys/surveys.helpers";
import ErrorLogService from "../../services/error-log/error-log.service";
import { apiUrl } from 'src/constants/config';
import ReduxService from 'src/redux/redux.service';
import { removeLoadingNotification } from '../notification/notification.slice';
import axios from 'axios';
import { RootState } from '../redux-store';

/** Registers survey group B2B endpoints on the shared API slice. */
import './survey-groups.endpoints';

const getUrl = (url: string) => {
  const state = ReduxService.Store.getState() as RootState;
  const isClientActive = state.userState.activeClient;
  return isClientActive ? url.replace('b2b', 'client') : url;
};

const getClientId = () => {
  const state = ReduxService.Store.getState() as RootState;
  const isClientActive = state.userState.activeClient;
  return isClientActive ? state.userState.activeClient?.ext_client_id ?? null : null;
};

// Audience endpoint used by AI Panelists filtering UI.
// Example: v1/b2b/surveys/audience returns panel_fields with selector_configuration.
export interface SurveyAudiencePanelFieldOption {
  label: string;
  value: string;
}

export interface SurveyAudiencePanelFieldSelectorControl {
  type: string;
  selection?: string;
  options?: SurveyAudiencePanelFieldOption[];
}

export interface SurveyAudiencePanelFieldSelectorConfiguration {
  control?: SurveyAudiencePanelFieldSelectorControl;
  options?: SurveyAudiencePanelFieldOption[];
}

export interface SurveyAudiencePanelField {
  id: string;
  name: string;
  display_name: string;
  status?: string;
  panel?: string;
  selector_configuration?: SurveyAudiencePanelFieldSelectorConfiguration;
}

export interface SurveyAudienceWithPanelFields {
  id: string;
  name: string;
  panel_type: string;
  status: string;
  locked?: boolean;
  panel_fields: SurveyAudiencePanelField[];
}

export interface SurveyAudiencePreviewResponse {
  panel_id: string;
  criteria: Record<string, string[]>;
  total_panelists: number;
  preview_count: number;
}


const surveyEndpoints = invisiblyApi.injectEndpoints({
  endpoints: (builder) => ({
    getSurveys: builder.query<FetchSurveyData, FetchSurveyDataRequest>({
      query: (data) => {
        return {
          method: 'get',
          params: {
            sort_by: data.sort_by ?? 'created_at',
            sort_order: 'desc',
            survey_status: data.survey_status,
            page: data.page,
            page_size: data.page_size ?? SURVEY_GRID_PAGE_SIZE,
            title: data.title,
            user: data.user,
            client_id: getClientId(),
          },
          url: getUrl('b2b/survey'),
        };
      },
      providesTags: [{ type: APITagType.SurveyList }],
      transformResponse(response: InvisiblyApiResponse<FetchSurveyApiResponse>) {
        return {
          surveys: response.item.items.map(s => SurveyService.parseListSurvey(s)),
          totalObjects: response.item.total_objects,
          totalPages: response.item.total_pages,
        };
      },
    }),

    // must use the ext_survey_id to get the survey, not the id
    getSurvey: builder.query<SurveyFull, { ext_survey_id: string, share?: boolean }>({
      query: ({ ext_survey_id, share }) => {
        return {
          method: 'get',
          params: {
            client_id: getClientId(),
          },
          url: createShare(getUrl(`b2b/survey/${ext_survey_id}`), share, 'b2b'),
        };
      },
      serializeQueryArgs: ({ queryArgs: { ext_survey_id } }) => ({ ext_survey_id }),
      transformResponse(response: { item: SurveyFullJSON }): SurveyFull {
        return SurveyService.parseSurvey(response.item);
      },
    }),

    getSurveyStats: builder.query<SurveyStats, { ext_survey_id: string, share?: boolean, selected_survey_answers?: string[] }>({
      query: ({ ext_survey_id, share, selected_survey_answers }) => ({
        method: 'get',
        params: {
          all: true,
          selected_survey_answers: selected_survey_answers?.join(','),
          client_id: getClientId(),
        },
        url: createShare(getUrl(`b2b/survey/${ext_survey_id}/stats`), share ?? false, 'b2b'),
      }),
      serializeQueryArgs: ({ queryArgs: { ext_survey_id, selected_survey_answers } }) => ({
        ext_survey_id,
        selected_survey_answers: selected_survey_answers?.join(','),
      }),
      transformResponse(response: BaseSuccessResponse): SurveyStats {
        const raw = response.item != null ? response.item : response;
        return SurveyService.parseSurveyStats(raw as SurveyStatsJSON);
      },
    }),

    getSurveyAudiences: builder.query<{ audiences: SurveyAudience[] }, null>({
      query: () => ({
        method: 'get',
        params: {
          client_id: getClientId(),
        },
        url: getUrl('b2b/audience'),
      }),
      transformResponse(response: { item: SurveyAudienceJSON[] }) {
        return {
          audiences: response.item.map((a: SurveyAudienceJSON) => SurveyService.parseAudience(a)),
        };
      },
    }),

    getSurveyAudiencesWithPanelFields: builder.query<SurveyAudienceWithPanelFields[], null>({
      query: () => ({
        method: 'get',
        params: {
          client_id: getClientId(),
        },
        url: getUrl('b2b/survey/audience'),
      }),
      transformResponse(response: any) {
        const raw = response?.item ?? response;
        return Array.isArray(raw) ? raw : raw.audiences;
      },
    }),

    previewSurveyAudience: builder.mutation<SurveyAudiencePreviewResponse, { panel: string; criteria: Record<string, string[]> }>({
      query: ({ panel, criteria }) => ({
        method: 'post',
        params: {
          client_id: getClientId(),
        },
        url: getUrl('b2b/survey/audience/preview'),
        data: {
          panel,
          criteria,
        },
      }),
      transformResponse(response: any): SurveyAudiencePreviewResponse {
        const raw = response?.item ?? response;
        return {
          panel_id: raw?.preview?.panel_id ?? '',
          criteria: raw?.preview?.criteria ?? {},
          total_panelists: Number(raw?.preview?.total_panelists ?? 0),
          preview_count: Number(raw?.preview?.preview_count ?? 0),
        };
      },
    }),

    //return not structured properly, needs status and item
    getSurveyAudience: builder.query<SurveyAudienceSmall, { id: string, isShare?: boolean }>({
      query: ({ id, isShare }) => ({
        method: 'get',
        params: {
          client_id: getClientId(),
        },
        url: createShare(getUrl(`b2b/audience/${id}`), isShare, 'b2b'),
      }),
      serializeQueryArgs: ({ queryArgs: { id } }) => ({ id }),
      transformResponse(response: InvisiblyApiResponse<SurveyAudienceSmallJSON>) {
        const data = SurveyService.parseAudienceSmall(response.item);

        if (data && !data.criteria) {
          // This can happen if audiences are manually created
          ErrorLogService.logError(Error('Invalid criteria from server'), 'results-targeting.component', 'selectFromResult', [data]);
        }

        return data;
      },
    }),

    getSurveyShareLink: builder.query<string, { ext_survey_id: string }>({
      query: ({ ext_survey_id }) => ({
        method: 'get',
        params: {
          client_id: getClientId(),
        },
        url: getUrl(`b2b/survey/${ext_survey_id}/share-link`),
      }),
      transformResponse(response: InvisiblyApiResponse<{ link: string }>) {
        return response.item.link;
      },
    }),

    getSurveyType: builder.query<SurveyTypeResponse, { ext_survey_id: string; share?: boolean }>({
      query: ({ ext_survey_id, share }) => ({
        method: 'get',
        params: {
          client_id: getClientId(),
        },
        url: createShare(getUrl(`b2b/survey/${ext_survey_id}/type`), share ?? false, 'b2b'),
      }),
      transformResponse(response: InvisiblyApiResponse<SurveyTypeResponse>) {
        return response.item;
      },
    }),

    getSurveyDemographics: builder.query<SurveyDemographicRecord[], { ext_survey_id: string; share?: boolean }>({
      query: ({ ext_survey_id, share }) => ({
        method: 'get',
        params: {
          client_id: getClientId(),
        },
        url: createShare(getUrl(`b2b/survey/${ext_survey_id}/demographics`), share ?? false, 'b2b'),
      }),
      transformResponse(response: InvisiblyApiResponse<SurveyDemographicsResponse>) {
        const body = response.item;
        const list = body?.answers;
        return Array.isArray(list) ? list : [];
      },
    }),

    getSurveyBrands: builder.query<[Brand], void>({
      query: () => ({
        method: 'get',
        params: {
          client_id: getClientId(),
        },
        url: getUrl('b2b/brands'),
      }),
      transformResponse(response: InvisiblyApiResponse<[Brand]>) {
        return response?.item;
      },
    }),
    getSurveyToasts: builder.query<SurveyToast[], { ext_survey_id: string }>({
      query: ({ ext_survey_id }) => ({
        method: 'GET',
        params: {
          client_id: getClientId(),
        },
        url: getUrl(`b2b/toasts?survey=${ext_survey_id}`),
      }),
      transformResponse(response: InvisiblyApiResponse<[SurveyToast]>, _meta, arg) {
        const { ext_survey_id } = arg;
        return response?.item?.map(toast => ({
          ...toast,
          survey: ext_survey_id,
        }));
      },
    }),

    // At this time, we only can set the title for a survey during a post call (image_url will be set later)
    createSurvey: builder.mutation<Survey, { title: string, human_sample_size: number, ai_sample_size: number, client_id: string | null }>({
      query: (data) => ({
        method: 'post',
        params: {
          client_id: getClientId(),
        },
        data: { title: data.title, max_completes: data.human_sample_size, ai_max_completes: data.ai_sample_size, client_id: data.client_id },
        url: getUrl('b2b/survey'),
      }),
      transformResponse(response: InvisiblyApiResponse<SurveyJSON>) {
        const parsedSurvey = SurveyService.parseListSurvey(response.item);

        return {
          ...parsedSurvey,
          ext_survey_id: response.item.ext_survey_id,
        };
      },
      invalidatesTags: [APITagType.SurveyList],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        let patchResult;
        try {
          const { data } = await queryFulfilled;
          const ext_survey_id = data.extSurveyId;

          patchResult = dispatch(
            surveyEndpoints.util.updateQueryData('getSurvey', { ext_survey_id }, (draft) => {
              Object.assign(draft, data);
            })
          );
        } catch (error) {
          console.error('Error creating survey:', error);
          if (patchResult) {
            patchResult.undo();
          }
        }
      },
    }),

    createSurveyAudience: builder.mutation<SurveyAudience, { criteria: SurveyAudienceCriteria, survey: string }>({
      query: (data) => ({
        method: 'post',
        data: data,
        params: {
          client_id: getClientId(),
        },
        url: getUrl('b2b/audience'),
      }),
      transformResponse(response: InvisiblyApiResponse<SurveyAudienceJSON>) {
        return SurveyService.parseAudience(response.item);
      },
      async onQueryStarted({ survey: ext_survey_id }, { dispatch, queryFulfilled }) {
        let patchResult: any;

        try {
          const { data } = await queryFulfilled;

          patchResult = dispatch(
            surveyEndpoints.util.updateQueryData('getSurvey', { ext_survey_id }, (draft) => {
              const patch = draft;
              patch.audience = data.id;
              Object.assign(draft, patch);
            })
          );
        } catch (error) {
          console.error('Error updating survey:', error);

          if (patchResult) {
            patchResult.undo();
          }
        }
      },
    }),

    createQuestion: builder.mutation<Question, QuestionCreationData>({
      query: (data) => ({
        method: 'post',
        params: {
          client_id: getClientId(),
        },
        url: getUrl('b2b/survey/question'),
        data: { ...data, shuffle: !data.shuffle ? 'no_shuffle' : 'random' },
      }),
      transformResponse(response: InvisiblyApiResponse<SurveyQuestionJSON>) {
        return {
          ...SurveyService.parseQuestion(response.item),
          id: response.item.id,
        };
      },
      async onQueryStarted({ ext_survey_id }, { dispatch, queryFulfilled }) {
        let patchResult;
        try {
          const { data } = await queryFulfilled;

          patchResult = dispatch(
            surveyEndpoints.util.updateQueryData('getSurvey', { ext_survey_id }, (draft) => {
              draft.questions.push(data);
            })
          );
          await queryFulfilled;
        } catch (error) {
          console.error('Error creating question:', error);
          if (patchResult) {
            patchResult.undo();
          }
        }
      },
    }),

    createAnswer: builder.mutation<Answer, { title: string, survey: string, survey_question: string, ext_survey_id: string }>({
      query: (data) => ({
        method: 'post',
        params: {
          client_id: getClientId(),
        },
        url: getUrl('b2b/survey/question/answer'),
        data: data,
      }),
      transformResponse(response: InvisiblyApiResponse<SurveyAnswerJSON>) {
        return { ...SurveyService.parseAnswer(response.item) };
      },
      async onQueryStarted({ ext_survey_id, survey_question }, { dispatch, queryFulfilled }) {
        let patchResult;
        try {
          const { data } = await queryFulfilled;
          patchResult = dispatch(
            surveyEndpoints.util.updateQueryData('getSurvey', { ext_survey_id }, (draft) => {
              const question = draft.questions.find((q) => q.id === survey_question);
              if (question) {
                const updatedAnswers = [...question.answers, data];
                question.answers = updatedAnswers;
              } else {
                console.error(`Question with id ${survey_question} not found`);
              }
            })
          );
          await queryFulfilled;
        } catch (error) {
          console.error('Error creating answer:', error);
          if (patchResult) {
            patchResult.undo();
          }
        }
      },
    }),
    createBrand: builder.mutation<Brand, Partial<Brand>>({
      query: (data) => ({
        method: "POST",
        params: {
          client_id: getClientId(),
        },
        url: getUrl('b2b/brands'),
        data: data,
      }),
      transformResponse(response: InvisiblyApiResponse<Brand>) {
        return response.item;
      },
    }),
    createToast: builder.mutation<SurveyToast, Partial<SurveyToast>>({
      query: (data) => ({
        method: 'post',
        params: {
          client_id: getClientId(),
        },
        url: getUrl('b2b/toasts'),
        data: data,
      }),
      transformResponse(response: InvisiblyApiResponse<SurveyToast>) {
        return response.item;
      },
    }),
    uploadImage: builder.mutation<UploadImage, { formData: FormData, uuid: string }>({
      query: ({ formData }) => ({
        method: 'POST',
        params: {
          client_id: getClientId(),
        },
        url: getUrl('b2b/image'),
        data: formData,
      }),
      async onQueryStarted({ uuid: ext_survey_id }, { dispatch, queryFulfilled }) {
        let patchResult: any;
        try {
          const { data } = await queryFulfilled;
          if (data.item.type === 'brand_1' || data.item.type === 'brand_2' || data.item.type === 'toast' || data.item.type === 'default_toast') {
            return;
          }
          patchResult = dispatch(
            surveyEndpoints.util.updateQueryData('getSurvey', { ext_survey_id }, (draft) => {
              if (data.item.type === 'survey') {
                draft.imageUrl = data.item.url;
              } else if (data.item.type === 'question') {
                const question = draft.questions.find(q => q.id === data.item.obj_uuid);
                if (question) {
                  question.imageUrl = data.item.url;
                }
              } else if (data.item.type === 'answer') {
                const question = draft.questions.find(q =>
                  q.answers.some(a => a.id === data.item.obj_uuid)
                );
                if (question) {
                  const answer = question.answers.find(a => a.id === data.item.obj_uuid);
                  if (answer) {
                    answer.imageUrl = data.item.url;
                    if (answer.title === DEFAULT_ANSWER_TITLE) {
                      answer.title = '';
                    }
                  }
                }
              }
            })
          );

        } catch (error) {
          console.error('Error uploading image:', error);
          if (patchResult) {
            patchResult.undo();
          }
        }
      },
    }),
    uploadCSV: builder.mutation<SurveyFull, { ext_survey_id: string, file: File }>({
      query: ({ ext_survey_id, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          method: 'PATCH',
          params: {
            client_id: getClientId(),
          },
          url: getUrl(`b2b/survey/${ext_survey_id}/csv/upload`),
          data: formData,
        };
      },
      transformResponse(response: InvisiblyApiResponse<SurveyFullJSON>): SurveyFull {
        return SurveyService.parseSurvey(response.item);
      },
      async onQueryStarted({ ext_survey_id }, { dispatch, queryFulfilled }) {
        let patchResult;
        try {
          const { data } = await queryFulfilled;
          patchResult = dispatch(
            surveyEndpoints.util.updateQueryData('getSurvey', { ext_survey_id }, (draft) => {
              Object.assign(draft, data);
            })
          );
        } catch (error) {
          console.error('Error uploading CSV:', error);
          if (patchResult) {
            patchResult.undo();
          }
        }
      },
    }),

    deleteImage: builder.mutation<void, { image_id: string, uuid: string, type: string }>({
      query: ({ image_id, type }) => ({
        method: 'DELETE',
        params: {
          client_id: getClientId(),
        },
        url: getUrl(`b2b/image/${image_id}?type=${type}`),
      }),
      async onQueryStarted({ uuid: ext_survey_id, image_id, type }, { dispatch, queryFulfilled }) {
        let patchResult: any;
        try {
          await queryFulfilled;
          if (type === 'brand_1' || type === 'brand_2') {
            return;
          }
          patchResult = dispatch(
            surveyEndpoints.util.updateQueryData('getSurvey', { ext_survey_id }, (draft) => {
              if (type === 'survey') {
                draft.imageUrl = null;
              } else if (type === 'question') {
                const question = draft.questions.find(q => q.id === image_id);
                if (question) {
                  question.imageUrl = null;
                }
              } else if (type === 'answer') {
                const question = draft.questions.find(q =>
                  q.answers.some(a => a.id === image_id)
                );
                if (question) {
                  const answer = question.answers.find(a => a.id === image_id);
                  if (answer) {
                    answer.imageUrl = null;
                  }
                }
              }
            })
          );
        } catch (error) {
          console.error('Error deleting image:', error);
          if (patchResult) {
            patchResult.undo();
          }
        }
      },
    }),

    moderationCheck: builder.mutation<ModerationCheckResults, { extSurveyId: string }>({
      query: ({ extSurveyId }) => ({
        url: getUrl(`b2b/survey/${extSurveyId}/review`),
        method: 'post',
        params: {
          client_id: getClientId(),
        },
        data: {},
      }),
      invalidatesTags: [APITagType.SurveyList],
      transformResponse(response: ModerationResponse) {
        return SurveyService.parseModerationResponse(response);
      },
      async onQueryStarted({ extSurveyId }, { dispatch, queryFulfilled }) {
        try {
          const response = await queryFulfilled;

          dispatch(
            surveyEndpoints.util.updateQueryData('getSurvey', { ext_survey_id: extSurveyId }, (draft) => {
              const patch = draft;
              // Now skipping moderation, goes straight to Live instead of Review
              patch.surveyStatus = response.data.didPass ? SurveyStatus.Live : SurveyStatus.Rejected;

              Object.assign(draft, patch);
            }));
        } catch (error) {
          console.error('moderation check error: ', error);
        }
      },
    }),
    cloneSurvey: builder.mutation<SurveyCloneResponse, { extSurveyId: string }>({
      query: ({ extSurveyId }) => ({
        url: getUrl(`b2b/survey/${extSurveyId}/clone`),
        method: 'post',
        params: {
          client_id: getClientId(),
        },
        data: {},
      }),
      invalidatesTags: [APITagType.SurveyList],
      transformResponse(response: {item: {ext_survey_id: string}}) {
        return {extSurveyId: response.item.ext_survey_id};
      },
      async onQueryStarted({ extSurveyId }, { dispatch, queryFulfilled }) {
        try {
          const response = await queryFulfilled;
        } catch (error) {
          console.error('clone survey error: ', error);
        }
      },
    }),

    updateSurveyAudience: builder.mutation<SurveyAudience, { id: string, data: Partial<SurveyAudienceJSON> }>({
      query: ({ id, data }) => {
        return {
          method: 'put',
          params: {
            client_id: getClientId(),
          },
          data: data,
          url: getUrl(`b2b/audience/${id}`),
        };
      },
      transformResponse(response: InvisiblyApiResponse<SurveyAudienceJSON>) {
        return SurveyService.parseAudience(response.item);
      },
      async onQueryStarted({ id, data }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          surveyEndpoints.util.updateQueryData('getSurveyAudience', { id }, draft => {
            const patch = draft;

            if (data.criteria) {
              patch.criteria = data.criteria;
            }

            Object.assign(draft, patch);
          })
        );

        try {
          await queryFulfilled;
        } catch (error) {
          console.error('Error updating survey audience:', error);
          patchResult.undo();
        }
      },
    }),

    updateSurvey: builder.mutation<Survey, { title: string; max_completes?: number; image_url?: string; ext_survey_id: string; survey_status?: string; category?: string; ai_max_completes?: number; brand?: Brand | null; survey_pixel?: string | null; survey_type?: 'ai' | 'rtr' | null; }>({
      query: (data) => {
        const payload = omitNullValues({
          title: data.title,
          ai_max_completes: data.ai_max_completes,
          max_completes: data.max_completes,
          image_url: data.image_url,
          survey_status: data.survey_status,
          category: data.category,
          brand: data.brand?.id ?? null,
          survey_pixel: data.survey_pixel ?? null,
          survey_type: data.survey_type ?? null,
        });
        if (data.brand !== undefined) {
          payload.brand = data.brand?.id ?? null; // Allow explicit `null` values
        }
        if (data.survey_pixel !== undefined) {
          payload.survey_pixel = data.survey_pixel ?? null; // Allow explicit `null` values
        }
        return {
          url: getUrl(`b2b/survey/${data.ext_survey_id}`),
          method: 'PATCH',
          params: {
            client_id: getClientId(),
          },
          data: payload,
        };
      },
      transformResponse: (response: InvisiblyApiResponse<SurveyJSON>) => {
        return SurveyService.parseListSurvey(response.item);
      },
      async onQueryStarted(
        { ext_survey_id, title, max_completes, image_url, survey_status, category, ai_max_completes, brand, survey_pixel, survey_type },
        { dispatch, queryFulfilled }
      ) {
        const patchResult = dispatch(
          surveyEndpoints.util.updateQueryData('getSurvey', { ext_survey_id }, (draft) => {
            const patch = draft;
            patch.title = title;
            if (max_completes !== undefined) {
              draft.maxCompletes = max_completes;
            }
            if (image_url !== undefined) {
              draft.imageUrl = image_url;
            }
            if (survey_status) {
              patch.surveyStatus = toSurveyStatus(survey_status);
            }
            if (category) {
              patch.category = category;
            }
            if (ai_max_completes !== undefined) {
              patch.maxAICompletes = ai_max_completes;
            }
            if (brand !== undefined) {
              patch.brand = brand;
            }
            if (survey_pixel !== undefined) {
              patch.surveyPixel = survey_pixel;
            }
            if (survey_type !== undefined) {
              patch.surveyType = survey_type;
            }
          })
        );
        try {
          await queryFulfilled;
        } catch (error) {
          console.error('Error updating survey:', error);
          patchResult.undo();
        }
      },
    }),
    updateQuestionOrder: builder.mutation<Survey, { questionArray?: string[], ext_survey_id: string }>({
      query: (data) => ({
        method: 'post',
        params: {
          client_id: getClientId(),
        },
        data: data.questionArray,
        url: getUrl(`b2b/survey/${data.ext_survey_id}/question/reorder`),
      }),
      async onQueryStarted({ questionArray, ext_survey_id }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          surveyEndpoints.util.updateQueryData('getSurvey', { ext_survey_id }, (draft) => {
            const patch = draft;
            questionArray?.forEach((questionId, index) => {
              const question = patch.questions.find((q) => q.id === questionId);
              if (question) {
                question.questionOrder = index;
                question.answers.forEach((answer) => {
                  answer.answerBranch = null;
                  answer.isFinalQuestion = false;
                });
              }
            });
            patch.questions.sort((a, b) => a.questionOrder - b.questionOrder);
            Object.assign(draft, patch);
          })
        );
        try {
          await queryFulfilled;
        } catch (error) {
          console.error('Error updateQuestionOrder:', error);
          patchResult.undo();
        }
      },
    }),

    updateQuestion: builder.mutation<Question, QuestionUpdateData>({
      query: (data) => ({
        method: 'patch',
        data,
        params: {
          client_id: getClientId(),
        },
        url: getUrl(`b2b/survey/question/${data.question_id}`),
      }),
      transformResponse(response: InvisiblyApiResponse<SurveyQuestionJSON>) {
        return {
          ...SurveyService.parseQuestion(response.item),
          id: response.item.id,
        };
      },
      async onQueryStarted({ ext_survey_id, title, question_id, image_url, shuffle, type, has_feedback }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          surveyEndpoints.util.updateQueryData('getSurvey', { ext_survey_id }, draft => {
            const question = draft.questions.find(q => q.id === question_id);
            if (question) {
              if (type) {
                question.type = type;

                if (type === SurveyQuestionType.MultiSelect) {
                  question.answers.forEach((answer) => {
                    answer.answerBranch = null;
                    answer.isFinalQuestion = false;
                  });
                } else if (type === SurveyQuestionType.Freeform) {
                  question.answers = [];
                }
              }
              question.title = title;
              if (image_url !== undefined) {
                question.imageUrl = image_url;
              }
              if (shuffle !== undefined) {
                question.shuffle = shuffle;
              }
              if (has_feedback !== undefined) {
                question.hasFeedback = has_feedback;
              }
            } else {
              console.error('Question not found:', question_id);
            }
          })
        );

        try {
          const { data } = await queryFulfilled;

          // Update the question in the store to account for changes from freeform to single or multiselect
          dispatch(
            surveyEndpoints.util.updateQueryData('getSurvey', { ext_survey_id }, draft => {
              const patch = draft;
              const questionIndex = patch.questions.findIndex(q => q.id === question_id);
              if (questionIndex > -1) {
                patch.questions[questionIndex] = data;
              }

              Object.assign(draft, patch);
            })
          );


          // Answers will be added to response, use these to create another patch to update the store

        } catch (error) {
          console.error('Error updating survey:', error);
          patchResult.undo();
        }
      },
    }),

    updateAnswer: builder.mutation<Answer, UpdateAnswerParams>({
      query: (data) => ({
        method: 'patch',
        data: data,
        params: {
          client_id: getClientId(),
        },
        url: getUrl(`b2b/survey/question/answer/${data.id}`),
      }),
      async onQueryStarted({ ext_survey_id, title, id, answer_branch, survey_question, is_final_question, answer_pixel, toast }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          surveyEndpoints.util.updateQueryData('getSurvey', { ext_survey_id }, draft => {
            const patch = draft;
            const answer = patch.questions.find(q => q.id === survey_question)?.answers.find(a => a.id === id);
            if (answer) {
              answer.title = title;
              if (answer_branch !== undefined) {
                answer.answerBranch = answer_branch;
              }
              if (is_final_question !== undefined) {
                answer.isFinalQuestion = is_final_question;
              }
              if (answer_pixel !== undefined) {
                answer.answerPixel = answer_pixel;
              }
              if (toast !== undefined) {
                answer.toast = toast;
              }
            } else {
              console.error('Answer not found:', id);
            }
            Object.assign(draft, patch);
          })
        );

        try {
          await queryFulfilled;
        } catch (error) {
          console.error('Error updating answer:', error);
          patchResult.undo();
        }
      },
    }),

    updateBrand: builder.mutation<Brand, Brand>({
      query: (brand) => ({
        method: 'PATCH',
        params: {
          client_id: getClientId(),
        },
        url: getUrl(`b2b/brands/${brand.id}`),
        data: brand,
      }),
      transformResponse(response: InvisiblyApiResponse<Brand>) {
        return response.item;
      },
    }),
    updateToast: builder.mutation<SurveyToast, SurveyToast>({
      query: (toast) => ({
        method: 'PATCH',
        params: {
          client_id: getClientId(),
        },
        url: getUrl(`b2b/toasts/${toast.id}`),
        data: toast,
      }),
      transformResponse(response: InvisiblyApiResponse<SurveyToast>) {
        return response.item;
      },
    }),

    deleteSurvey: builder.mutation<ApiStatusData, { ext_survey_id: string }>({
      query: (data) => ({
        method: 'DELETE',
        params: {
          client_id: getClientId(),
        },
        url: getUrl(`b2b/survey/${data.ext_survey_id}`),
        successCodes: [204],
      }),
      transformResponse(response: InvisiblyApiResponse<void>) {
        return response.status;
      },
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          console.error('Error deleting question:', error);
        }
      },
    }),

    deleteQuestion: builder.mutation<ApiStatusData, { ext_survey_id: string, question_id: string }>({
      query: (data) => ({
        method: 'DELETE',
        params: {
          client_id: getClientId(),
        },
        url: getUrl(`b2b/survey/question/${data.question_id}`),
        successCodes: [204],
      }),
      transformResponse(response: InvisiblyApiResponse<void>) {
        return response.status;
      },
      async onQueryStarted({ ext_survey_id, question_id }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          surveyEndpoints.util.updateQueryData('getSurvey', { ext_survey_id }, (draft) => {
            draft.questions = draft.questions.filter(q => q.id !== question_id);
          })
        );
        try {
          await queryFulfilled;
          const survey = await dispatch(surveyEndpoints.endpoints.getSurvey.initiate({ ext_survey_id }));
          const updatedQuestions = survey.data?.questions || [];
          const orderedQuestions = [...updatedQuestions].sort((a, b) => a.questionOrder - b.questionOrder);
          const questionArray = orderedQuestions.map((q) => q.id);


          if (orderedQuestions.length) {
            await dispatch(surveyEndpoints.endpoints.updateQuestionOrder.initiate({
              questionArray,
              ext_survey_id,
            }));
          }
        } catch (error) {
          console.error('Error deleting question:', error);
          patchResult.undo();
        }
      },
    }),

    deleteAnswer: builder.mutation<ApiStatusData, { ext_survey_id: string, question_id: string, answer_id: string }>({
      query: (data) => ({
        method: 'DELETE',
        params: {
          client_id: getClientId(),
        },
        url: getUrl(`b2b/survey/question/answer/${data.answer_id}`),
        successCodes: [204],
      }),
      transformResponse(response: InvisiblyApiResponse<void>) {
        return response.status;
      },
      async onQueryStarted({ ext_survey_id, question_id, answer_id }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          surveyEndpoints.util.updateQueryData('getSurvey', { ext_survey_id }, (draft) => {
            const question = draft.questions.find(q => q.id === question_id);
            if (question) {
              question.answers = question.answers.filter(a => a.id !== answer_id);
            }
          })
        );
        try {
          await queryFulfilled;
        } catch (error) {
          console.error('Error deleting answer:', error);
          patchResult.undo();
        }
      },
    }),
    deleteBrand: builder.mutation<ApiStatusData, { brand_id: string }>({
      query: (data) => ({
        method: 'DELETE',
        params: {
          client_id: getClientId(),
        },
        url: getUrl(`b2b/brands/${data.brand_id}`),
        successCodes: [204],
      }),
      transformResponse(response: InvisiblyApiResponse<void>) {
        return response.status;
      },
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          console.error('Error deleting brand:', error);
        }
      },
    }),
    getSurveySummary: builder.query<SurveySummaryData, { ext_survey_id: string, share?: boolean }>({
      query: ({ ext_survey_id, share }) => ({
        method: 'get',
        params: {
          client_id: getClientId(),
        },
        url: createShare(getUrl(`b2b/survey/${ext_survey_id}/summary`), share, 'b2b'),
      }),
      serializeQueryArgs: ({ queryArgs: { ext_survey_id } }) => ({ ext_survey_id }),
      transformResponse(response: InvisiblyApiResponse<SurveySummaryDataJSON>) {
        return SurveyService.parseSurveySummary(response.item);
      },
      async onCacheEntryAdded(
        { ext_survey_id },
        { cacheDataLoaded, cacheEntryRemoved, updateCachedData }
      ) {

        try {
          await cacheDataLoaded;
          removeLoadingNotification({
            id: "summary_notification_id",
            summary: "summary_notificaiton",
            theme: "info",
          });
        } catch (error) {
          console.error('Error fetching summary', error);
        }
        // Cleanup when the cache subscription is no longer active
        await cacheEntryRemoved;
      },
    }),
    shareSurveyResults: builder.mutation<string, string>({
      query: (surveyId) => ({
        method: 'post',
        params: {
          client_id: getClientId(),
        },
        url: getUrl(`b2b/survey/${surveyId}/share-results`),
      }),
      transformResponse(response: InvisiblyApiResponse<{ ext_survey_id: string, share_id: string }>) {
        return response?.item?.share_id ?? '';
      },
    }),

    generateAiSurveyResponses: builder.mutation<SurveyResponder, SurveyResponderJSON>({
      query: (data) => ({
        method: 'post',
        params: {
          client_id: getClientId(),
        },
        url: getUrl('b2b/panel/responses'),
        data: data,
      }),
      transformResponse(response: InvisiblyApiResponse<{ ext_survey_id: string, max_completes: string }>): SurveyResponder {
        return {
          extSurveyId: response.item.ext_survey_id,
          maxCompletes: response.item.max_completes,
        };
      },
    }),
    schedulePulsePolling: builder.mutation<PulsePollingConfigResponse, {
      interval: 'day' | 'week' | 'month',
      completes: number,
      start_date: string,
      end_date: string,
      survey: string
    }>({
      query: (data) => ({
        method: 'post',
        params: {
          client_id: getClientId(),
        },
        url: getUrl('b2b/survey/run/config'),
        data: data,
      }),
      invalidatesTags: (_result, _error, { survey }) => [
        { type: APITagType.PulsePollingConfig, id: survey },
      ],
      transformResponse(response: InvisiblyApiResponse<PulsePollingConfigResponse>) {
        return response.item;
      },
    }),
    getPulsePollingConfig: builder.query<PulsePollingConfigResponse | null, { ext_survey_id: string }>({
      query: ({ ext_survey_id }) => ({
        method: 'get',
        params: {
          client_id: getClientId(),
        },
        url: getUrl(`b2b/survey/run/config/run_config?ext_survey_id=${ext_survey_id}`),
        handleErrorGlobally: false,
      }),
      providesTags: (_result, _error, { ext_survey_id }) => [
        { type: APITagType.PulsePollingConfig, id: ext_survey_id },
      ],
      transformResponse(response: InvisiblyApiResponse<PulsePollingConfigResponse | null>) {
        return response.item;
      },
    }),
    updatePulsePollingConfig: builder.mutation<PulsePollingConfigResponse, {
      id: string,
      interval: 'day' | 'week' | 'month',
      completes: number,
      start_date: string,
      end_date: string,
    }>({
      query: ({ id, ...data }) => ({
        method: 'patch',
        params: {
          client_id: getClientId(),
        },
        url: getUrl(`b2b/survey/run/config/${id}`),
        data: data,
      }),
      invalidatesTags: (result) => [
        { type: APITagType.PulsePollingConfig, id: result?.survey },
      ],
      transformResponse(response: InvisiblyApiResponse<PulsePollingConfigResponse>) {
        return response.item;
      },
    }),
    deletePulsePollingConfig: builder.mutation<void, { id: string, survey: string }>({
      query: ({ id }) => ({
        method: 'delete',
        params: {
          client_id: getClientId(),
        },
        url: getUrl(`b2b/survey/run/config/${id}`),
      }),
      invalidatesTags: (_result, _error, { survey }) => [
        { type: APITagType.PulsePollingConfig, id: survey },
      ],
    }),
  }),
  overrideExisting: true,
});

// Endpoints without status code return handled separately from RTK
export async function getRtrTag(ext_survey_id: string, share = false, size: string = '300x250'): Promise<string | null> {
  try {
    axios.defaults.baseURL = apiUrl;

    const state = ReduxService.Store.getState();
    const jwt = state?.userState?.access;

    const response = await axios.get(createShare(`rtr/tag/${ext_survey_id}?size=${size}`, share, null, true), {
      headers: jwt ? {
        'Authorization': `Bearer ${jwt}`,
      } : {},
    });

    if (response.status != 200) {
      console.error(`Failed to fetch tag. Status: ${response.status} - ${response.statusText}`);
      return null;
    }
    const data = await response.data;
    return data;
  } catch (error) {
    console.error('Error occurred while fetching RTR tag:', error);
    return null;
  }
}
export async function getSurveyFlowchart(ext_survey_id: string): Promise<string | null> {
  try {
    const state = ReduxService.Store.getState();
    const jwt = state?.userState?.access;
    const url = getUrl(`${apiUrl}/b2b/survey/${ext_survey_id}/flowchart`);
    const params = new URLSearchParams();
    params.set('client_id', getClientId() ?? '');
    const urlWithParams = `${url}?${params.toString()}`;

    const response = await fetch(urlWithParams, {
      method: 'GET',
      headers: jwt ? {
        'Authorization': `Bearer ${jwt}`,
      } : {},
    });
    if (!response.ok) {
      console.error(`Failed to fetch flowchart. Status: ${response.status} - ${response.statusText}`);
      return null;
    }
    const data = await response.text();
    return data;
  } catch (error) {
    console.error('Error occurred while fetching flowchart:', error);
    return null;
  }
}

export const {
  useUploadImageMutation,
  useUploadCSVMutation,
  useGetSurveysQuery,
  useGetSurveyStatsQuery,
  useGetSurveyQuery,
  useGetSurveyAudienceQuery,
  useGetSurveyAudiencesQuery,
  useGetSurveyAudiencesWithPanelFieldsQuery,
  usePreviewSurveyAudienceMutation,
  useLazyGetSurveyShareLinkQuery,
  useGetSurveyBrandsQuery,
  useGetSurveyToastsQuery,
  useCreateSurveyMutation,
  useCreateSurveyAudienceMutation,
  useUpdateSurveyAudienceMutation,
  useCreateQuestionMutation,
  useCreateAnswerMutation,
  useCreateBrandMutation,
  useCreateToastMutation,
  useUpdateSurveyMutation,
  useUpdateQuestionMutation,
  useUpdateQuestionOrderMutation,
  useModerationCheckMutation,
  useUpdateAnswerMutation,
  useUpdateBrandMutation,
  useUpdateToastMutation,
  useDeleteSurveyMutation,
  useDeleteQuestionMutation,
  useDeleteAnswerMutation,
  useDeleteImageMutation,
  useDeleteBrandMutation,
  useGetSurveySummaryQuery,
  useShareSurveyResultsMutation,
  useCloneSurveyMutation,
  useGenerateAiSurveyResponsesMutation,
  useSchedulePulsePollingMutation,
  useGetPulsePollingConfigQuery,
  useUpdatePulsePollingConfigMutation,
  useDeletePulsePollingConfigMutation,
  useGetSurveyTypeQuery,
  useGetSurveyDemographicsQuery,
} = surveyEndpoints;

export { surveyEndpoints };
